import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconDate } from '@components/icons/date';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { NoResultCard } from '@components/no-result-card';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { getRawTextFromHtml } from '@utils/browser';
import { abbreviateDate } from '@utils/date';
import { fileNameFromPath } from '@utils/strings';

import { useCalendarEvent } from 'api/calendar-events/hooks';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { ActionType, CalendarEventData, CalendarEventFormFields, formatFormData } from './common';

import {
  ActionResultCard,
  EditableActionContent,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  isActionError,
  useEditingContext,
} from '../common';
import { ActionTimeWithTimezoneTooltip } from '../timezone';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);

  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }

  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('calendarEvent')} icon={IconDate} iconColor="green100">
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const calendarEventQuery = useCalendarEvent(action.shortId);
  const { t } = useScopedTranslate(sidebarLocale);

  return (
    <Stack space={4}>
      <LabelTextGroup label={t('description')} text={action.description} />
      <CalendarEventDetailCard action={action} />
      <Sidebar.Separator />
      <HideContainer title={t('calendarEventOutput')}>
        {calendarEventQuery.data?.file ? (
          <UploadedFile
            name={fileNameFromPath(calendarEventQuery.data.file)}
            path={calendarEventQuery.data.file}
            iconColor="elementPurpleInvert"
            font="regular14"
          />
        ) : (
          <NoResultCard icon={IconDate} text={t('actionNotRun')} />
        )}
      </HideContainer>
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('create')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const CalendarEventDetailCard = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const isError = isActionError(action);
  const status = getActionStatus(action);

  const durationMinutes = action.data?.durationMinutes;
  const durationHours = action.data?.durationHours;
  const [offset] = action?.timezone?.split(' ') ?? [''];

  const progressText =
    status === 'completed'
      ? t('calendarEventCreated')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  const referenceDate = action.data.date ? new Date(action.data.date) : undefined;
  if (referenceDate) {
    referenceDate.setHours(action.data?.hour);
    referenceDate.setMinutes(action.data?.minute);
  }
  const description = action.data?.description;

  const labelTextGroups = {
    title: { label: t('title'), text: getRawTextFromHtml(action.data?.title) },
    description: {
      label: t('description'),
      text: description ? getRawTextFromHtml(description) : '',
    },
    location: { label: t('location'), text: action.data?.location },
    organizer: { label: t('organizer'), text: action.data?.organizer },
    attendees: { label: t('attendees'), text: action.data?.attendees },
    sendEmailToAttendees: {
      label: t('sendEmailToAttendees'),
      text: action.data?.sendEmailToAttendees ? 'Yes' : 'No',
    },
    date: {
      label: t('date'),
      text: referenceDate ? abbreviateDate(referenceDate, 'en', 'datetime') : undefined,
    },
  };
  return (
    <ActionResultCard
      action={action}
      title={t('calendarEvent')}
      icon={<IconDate />}
      progressText={progressText}
      errorTitle={actionErrorTitle[action.action]}
    >
      {Object.entries(labelTextGroups).map(([key, { label, text }]) => {
        if (!text) {
          return <LabelTextGroup key={key} label={label} />;
        }
        if (key !== 'date') {
          return <LabelTextGroup key={key} label={label} text={text} />;
        }
        return (
          <div key={key}>
            <Text font="semibold14" text={label} />
            <ActionTimeWithTimezoneTooltip
              actionText={text}
              hour={action?.values?.hour?.value as string}
              minute={action?.values?.minute?.value as string}
              journeyTimezoneOffset={offset}
            />
          </div>
        );
      })}

      <Stack space={1}>
        <Text font="semibold14" text="Duration" />

        {durationMinutes === '0' && durationHours === '0' ? (
          <LabelTextGroup label="" />
        ) : (
          <>
            <Text
              font="semibold14"
              text={
                durationHours === '1' ? t('hourConfig') : t('hoursConfig', { total: durationHours })
              }
            />
            <Text
              font="semibold14"
              text={
                durationMinutes === '1'
                  ? t('minuteConfig')
                  : t('minutesConfig', { total: durationMinutes })
              }
            />
          </>
        )}
      </Stack>
    </ActionResultCard>
  );
};

const Form = createForm<CalendarEventData>();

const FormContent = ({ action }: ContentProps) => {
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const { stopEditing } = useEditingContext();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: CalendarEventData) => {
    return editMutation.mutateAsync(formatFormData(data));
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <CalendarEventFormFields Form={Form} data={action} />
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    calendarEvent: 'Calendar event',
    dataNotFound: 'Data not found',
    description: 'Description',
    inProgress: 'In progress',
    calendarEventCreated: 'Calendar event created',
    error: 'Error',
    notStarted: 'Not started',
    title: 'Title',
    location: 'Location',
    organizer: 'Organizer',
    attendees: 'Attendees',
    date: 'Date',
    atHour: 'At hour',
    atMinute: 'At minutes',
    duration: 'Duration',
    calendarEventOutput: 'Calendar event output',
    actionNotRun: 'Action has not been run',
    hourConfig: '1 • Hour',
    hoursConfig: '{total} • Hours',
    minuteConfig: '1 • Minute',
    minutesConfig: '{total} • Minutes',
    create: 'Create',
    retry: 'Re-create',
    sendEmailToAttendees: 'Send email to attendees?',
  },
};
